import moment from 'moment'
import { InstructorUnAvailability } from '@interfaces/InstructorUnAvailability'
import { convertMomentToDate } from '@utils/helpers'
import { DATE_FORMAT } from '@utils/constants'
import { ACTIONS, DEFAULTS, REPEAT_OPTIONS } from '@utils/constants/availability'
import PortalInstructor from './Instructor'

moment.locale('en', {
  week: {
    dow: 1,
    doy: 1,
  },
})

class PortalInstructorUnAvailability {
  unAvailability: InstructorUnAvailability
  Instructor: PortalInstructor

  constructor(unAvailability: InstructorUnAvailability, Instructor: PortalInstructor) {
    this.unAvailability = unAvailability || null
    this.Instructor = Instructor
  }

  get InstructorUnAvailabilityInfo() {
    return this.unAvailability
  }

  get Availability() {
    return null
  }

  get UnAvailability() {
    return this.unAvailability?.InstructorUnAvailabilityInfo
  }

  get Id() {
    return this.UnAvailability?.Id || ''
  }

  get InstructorProfileId() {
    return this.UnAvailability?.InstructorProfileId
  }

  get IsAvailability() {
    return false
  }

  get IsUnAvailability() {
    return true
  }

  get StartDate() {
    return this.UnAvailability?.StartDaterTime
  }

  get StartDateTime() {
    return this.UnAvailability?.StartDaterTime
  }

  get EndDateTime() {
    return this.UnAvailability?.EndDateTime
  }

  get IsEndTimeEndOfDay() {
    return (
      moment(this.EndDateTime)
        .tz(this.Instructor.TimeZone)
        .format(DATE_FORMAT.HM) === DEFAULTS.END_TIME_SAVE
    )
  }

  get ApprovalStatus() {
    return this.UnAvailability?.ApprovalStatus
  }

  get IsFlex() {
    return false
  }

  get LearningCenterId() {
    return this.UnAvailability?.LearningCenterId || ''
  }

  get LearningCenterName() {
    return this.UnAvailability?.LearningCenterName || ''
  }

  get Type() {
    return ''
  }

  get KeepExistingLessons() {
    return this.UnAvailability?.KeepExistingLessons
  }

  get AllDay() {
    return this.UnAvailability.AllDay || false
  }

  get UnAvailabilityType() {
    return this.UnAvailability.UnAvailabilityType || ''
  }

  get IsCore() {
    return false
  }

  get IsF2F() {
    return false
  }

  get TimeZone() {
    return this.UnAvailability.TimeZone || moment.tz.guess()
  }

  get CalendarTitle() {
    return this.UnAvailability?.UnAvailabilityType
  }

  get CalendarStart() {
    return convertMomentToDate(moment(this.StartDateTime))
  }

  get CalendarEnd() {
    const EndDateTime = moment(this.EndDateTime)
    return convertMomentToDate(this.IsEndTimeEndOfDay ? EndDateTime.subtract(1, 'minute') : EndDateTime)
  }

  get CalendarEvent() {
    return {
      id: this.Id,
      title: this.CalendarTitle,
      start: this.CalendarStart,
      end: this.CalendarEnd,
      resource: new PortalInstructorUnAvailability(this.unAvailability, this.Instructor),
    }
  }

  get TypeClassName() {
    return 'timeoff'
  }

  get TimeClassName() {
    return `time-${moment(this.CalendarStart).format(DATE_FORMAT.HHmm)} `
  }

  get FormStartTime() {
    const StartTime = moment(this.CalendarStart).tz(this.TimeZone)
    return moment(StartTime.format(DATE_FORMAT.HHmm), DATE_FORMAT.HHmm)
  }

  get FormEndTime() {
    const EndTime = moment(this.CalendarEnd).tz(this.TimeZone)
    return moment(EndTime.format(DATE_FORMAT.HHmm), DATE_FORMAT.HHmm)
  }

  get FormValues() {
    return {
      StartTime: this.FormStartTime,
      EndTime: this.FormEndTime,
      AllDay: this.AllDay,
      LessonType: '',
      AvailabilityType: '',
      StartDate: moment(this.CalendarStart).tz(this.Instructor.TimeZone),
      EndDate: moment(this.CalendarEnd).tz(this.Instructor.TimeZone),
      RepeatOn: REPEAT_OPTIONS.map(({ value }) => value),
      KeepLessons: this.KeepExistingLessons,
      UnAvailabilityType: this.UnAvailabilityType,
      Id: this.Id,
      LearningCenterId: this.LearningCenterId,
      LearningCenterName: this.LearningCenterName,
    }
  }

  isPastEvent = (date: Date) => {
    return moment(this.CalendarStart).isBefore(date)
  }

  isDeletable = () => true

  getCalendarEventClassName(instructorCurrentDate: moment.Moment | Date, isAdmin?: boolean) {
    return `${this.TypeClassName} ${this.getPastTimeClassName(instructorCurrentDate)} ${this.getPointerClassname(
      instructorCurrentDate,
      isAdmin
    )} ${this.TimeClassName}`
  }

  getPastTimeClassName(instructorCurrentDate: moment.Moment | Date) {
    return moment(this.CalendarStart).isBefore(instructorCurrentDate) ? 'past-event' : ''
  }

  getPointerClassname(instructorCurrentDate: moment.Moment | Date, isAdmin?: boolean) {
    return isAdmin && moment(this.CalendarStart).isAfter(instructorCurrentDate) ? 'cursor' : ''
  }

  getDeletePayload(KeepExistingLessons: boolean) {
    return {
      InstructorUnAvailabilityInfo: {
        Id: this.Id,
        Action: ACTIONS.DELETE,
        KeepExistingLessons,
      },
    }
  }
}

export default PortalInstructorUnAvailability
